import React, { useState, useCallback } from 'react';
import { 
  Card, Button, Form, Alert, Row, Col, 
  ProgressBar, InputGroup, Modal
} from 'react-bootstrap';
import { uploadLeads, downloadTemplate } from '../../services/api';

// Maximum rows allowed in CSV file
const MAX_ROWS = 500000; // 5 lakh rows

// Required columns in CSV file
const REQUIRED_COLUMNS = ['name', 'mobile'];

// Optional but recommended columns
const OPTIONAL_COLUMNS = ['pan', 'email', 'address', 'city', 'pincode', 'income'];

const UploadPage = () => {
  const [file, setFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState('');
  const [warningMessage, setWarningMessage] = useState('');
  const [showTooLargeModal, setShowTooLargeModal] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [fileValidated, setFileValidated] = useState(false);
  const [missingColumns, setMissingColumns] = useState([]);
  
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    
    if (!selectedFile) return;
    
    // Store the file and validate it
    setFile(selectedFile);
    validateCsvFile(selectedFile);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!file) {
      setError('Please select a file to upload.');
      return;
    }
    
    // Check if the file has been validated
    if (!fileValidated) {
      setError('Please wait for file validation to complete.');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const result = await uploadLeads(file);
      setUploadStatus(result);
    } catch (err) {
      console.error('Error uploading leads:', err);
      setError('An error occurred while uploading the file. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    
    const droppedFile = e.dataTransfer.files[0];
    
    if (!droppedFile) return;
    
    // Store the file and validate it
    setFile(droppedFile);
    validateCsvFile(droppedFile);
  };
  
  const handleDragOver = (e) => {
    e.preventDefault();
  };
  
  const resetUpload = () => {
    setFile(null);
    setUploadStatus(null);
    setError('');
    setWarningMessage('');
    setFileValidated(false);
    setMissingColumns([]);
    setRowCount(0);
  };
  
  const validateCsvFile = useCallback((file) => {
    if (!file) return;
    
    // Reset validation states
    setError('');
    setWarningMessage('');
    setMissingColumns([]);
    setFileValidated(false);
    setValidating(true);
    
    // Check if file is CSV
    if (file.name.endsWith('.csv') || file.type === 'text/csv') {
      // Read the file content
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const content = e.target.result;
          const lines = content.split('\n');
          
          // Get headers from the first line
          const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
          
          // Check for required columns
          const missing = REQUIRED_COLUMNS.filter(col => 
            !headers.includes(col.toLowerCase())
          );
          
          if (missing.length > 0) {
            setMissingColumns(missing);
            setError(`Missing required columns: ${missing.join(', ')}`);
            setFile(null);
            setValidating(false);
            return;
          }
          
          // Count rows (subtract 1 for the header row)
          const count = lines.length > 1 ? lines.length - 1 : 0;
          setRowCount(count);
          
          // Check if file has too many rows
          if (count > MAX_ROWS) {
            setShowTooLargeModal(true);
            setValidating(false);
            return;
          }
          
          // Check for optional columns - add warning if missing
          const missingOptional = OPTIONAL_COLUMNS.filter(col => 
            !headers.includes(col.toLowerCase())
          );
          
          if (missingOptional.length > 0) {
            setWarningMessage(
              `Note: Your file is missing some recommended columns: ${missingOptional.join(', ')}`
            );
          }
          
          setFileValidated(true);
          setValidating(false);
        } catch (err) {
          console.error('Error validating CSV:', err);
          setError('Invalid CSV format. Please check your file and try again.');
          setFile(null);
          setValidating(false);
        }
      };
      
      reader.onerror = () => {
        setError('Error reading file. Please try again.');
        setFile(null);
        setValidating(false);
      };
      
      // Read the file as text
      reader.readAsText(file);
    } else {
      setError('Please upload a CSV file. Excel files are no longer supported.');
      setFile(null);
      setValidating(false);
    }
  }, []);

  const handleDownloadTemplate = async () => {
    try {
      await downloadTemplate();
    } catch (err) {
      setError('Failed to download template. Please try again.');
    }
  };
  
  // Handle closing the too large file modal
  const handleCloseTooLargeModal = () => {
    setShowTooLargeModal(false);
    setFile(null);
  };

  return (
    <div>
      <h2>Upload Lead Data</h2>
      <p className="text-muted">Upload lead data in CSV format.</p>
      
      {/* Modal for files with too many rows */}
      <Modal show={showTooLargeModal} onHide={handleCloseTooLargeModal}>
        <Modal.Header closeButton>
          <Modal.Title>File Too Large</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>The CSV file contains {rowCount.toLocaleString()} rows, which exceeds the maximum limit of {MAX_ROWS.toLocaleString()} rows.</p>
          <p>Please split your data into multiple smaller files before uploading.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleCloseTooLargeModal}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>
      
      <Card className="mb-4">
        <Card.Body>
          {uploadStatus ? (
            <>
              <Alert variant="success">
                <Alert.Heading>Upload Complete!</Alert.Heading>
                <p>{uploadStatus.message || "Your file has been processed."}</p>
              </Alert>
              
              <Row className="text-center">
                <Col>
                  <h5>Total Records</h5>
                  <p className="display-6">{uploadStatus.total_records}</p>
                </Col>
                <Col>
                  <h5>Valid Records</h5>
                  <p className="display-6 text-success">{uploadStatus.valid_records}</p>
                </Col>
                <Col>
                  <h5>Duplicate Records</h5>
                  <p className="display-6 text-warning">{uploadStatus.duplicate_records}</p>
                </Col>
                <Col>
                  <h5>Invalid Records</h5>
                  <p className="display-6 text-danger">{uploadStatus.invalid_records}</p>
                </Col>
              </Row>
              
              <div className="d-grid gap-2 mt-4">
                <Button variant="primary" onClick={resetUpload}>
                  Upload Another File
                </Button>
              </div>
            </>
          ) : (
            <Form onSubmit={handleSubmit}>
              {error && <Alert variant="danger">{error}</Alert>}
              
              <div 
                className="upload-box mb-3"
                onDrop={handleDrop}
                onDragOver={handleDragOver}
              >
                {file ? (
                  <div className="text-center">
                    <h5 className="mb-3">{file.name}</h5>
                    <p className="text-muted">
                      {(file.size / 1024).toFixed(2)} KB • {validating ? 'Validating...' : fileValidated ? 'Valid CSV' : 'Validation pending'}
                    </p>
                    {fileValidated && (
                      <div className="mb-3">
                        <Alert variant="info">
                          <i className="bi bi-check-circle-fill me-2"></i>
                          CSV validated successfully. {rowCount.toLocaleString()} rows found.
                        </Alert>
                      </div>
                    )}
                    {warningMessage && (
                      <div className="mb-3">
                        <Alert variant="warning">{warningMessage}</Alert>
                      </div>
                    )}
                    <Button 
                      variant="outline-danger"
                      size="sm"
                      onClick={() => resetUpload()}
                    >
                      Remove File
                    </Button>
                  </div>
                ) : (
                  <div className="text-center">
                    <i className="bi bi-cloud-arrow-up-fill fs-1"></i>
                    <h5 className="mt-3">Drag & Drop File Here</h5>
                    <p className="text-muted">Or click to browse</p>
                    <InputGroup className="mt-3 justify-content-center">
                      <Form.Control
                        type="file"
                        accept=".csv"
                        onChange={handleFileChange}
                        style={{ maxWidth: '300px' }}
                      />
                    </InputGroup>
                  </div>
                )}
              </div>
              
              <div className="d-grid gap-2">
                <Button 
                  type="submit"
                  variant="primary"
                  disabled={!file || loading || validating || !fileValidated}
                >
                  {loading ? 'Uploading...' : validating ? 'Validating...' : 'Upload File'}
                </Button>
              </div>
              
              {loading && (
                <ProgressBar animated now={100} className="mt-3" />
              )}
            </Form>
          )}
        </Card.Body>
      </Card>
      
      <Card>
        <Card.Body>
          <Card.Title>Required Format</Card.Title>
          <Card.Text>
            Your CSV file must contain the following columns (max {MAX_ROWS.toLocaleString()} rows):
          </Card.Text>
          <ul>
            <li><strong>Name</strong> (Mandatory)</li>
            <li><strong>Mobile</strong> (Mandatory)</li>
            <li><strong>PAN</strong> (Optional, but required for CIBIL checks)</li>
            <li><strong>Email</strong> (Optional)</li>
            <li>Any other lead details you wish to include</li>
          </ul>
          <Card.Text>
            The system will validate required fields, perform deduplication based on PAN,
            and queue valid leads for CIBIL API processing.
          </Card.Text>
          <div className="mt-3">
            <Button 
              variant="outline-primary" 
              size="sm"
              onClick={handleDownloadTemplate}
            >
              <i className="bi bi-download me-1"></i>
              Download Template CSV
            </Button>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default UploadPage;